import React, { FC, useCallback, useEffect, useMemo } from "react";
import { GridItem } from "@chakra-ui/react";
import { ProductGrid } from "@ldp-web/component-library/ui/molecules/product-grid";
import { ProductTileSdm } from "@ldp-web/component-library/ui/molecules/product-tile";
import { LongText } from "@ldp-web/component-library/ui/molecules/long-text";
import { WrapperComponent } from "../wrapper";
import {
  useAnalyticsContext,
  useFeatureFlags,
  useSettingsContext,
} from "@ldp/common-contexts";
import {
  IHeaderGroupComponentProps,
  ISDMProductTileData,
  SDMIndicatorIdType,
  SDMLanguageType,
  TSDMPagination,
} from "@ldp/common-types";
import {
  trackMediaAnalyticsEvent,
  useAddToCartTrackEvent,
  usePaginationTracking,
} from "@ldp/sdm-analytics-utils";
import {
  useSDMCartContext,
  useSDMRouterContext,
  useSDMViewportContext,
} from "@ldp/sdm-contexts";
import { useTrackMediaAnalyticsAdClick } from "@ldp/media-analytics";
import { useGetA11yCartMessages } from "../../hooks";
import { useSimpleTranslation } from "@ldp/simple-i18n";
import { updateProductTiles, useRedirectLink } from "../../utils";
import { HeaderGroupComponent } from "../header-group";

/** simple product grid component props */
export interface ISimpleProductGridComponentProps {
  /** id for the product grid */
  id?: string;
  /** content id from BFF response */
  contentId?: string | null;
  /** content id from BFF response */
  componentId?: string;
  /** pagination data */
  pagination: TSDMPagination;
  /** product tile data */
  productTiles: ISDMProductTileData[];
  /** header group details */
  headerGroup?: IHeaderGroupComponentProps;
}
/** Load Product Grid component */
export const SimpleProductGridComponent: FC<
  ISimpleProductGridComponentProps
> = ({ pagination, productTiles, contentId, componentId, headerGroup, id }) => {
  const settings = useSettingsContext();
  const viewportContext = useSDMViewportContext();
  const { trackEventTileAdClick } = useAnalyticsContext();
  const i18n = useSimpleTranslation();
  const isDesktopView = viewportContext.viewport === "desktop";
  const trackPaginationFn = usePaginationTracking();
  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow } = getFeatureFlags();
  const trackMediaAnalyticsAdClick = useTrackMediaAnalyticsAdClick();
  const { addToCart, isLoading: isCartLoading, entries } = useSDMCartContext();
  const compData = useMemo(
    () => ({
      id: contentId || null,
      type: componentId || null,
      name: null,
    }),
    [contentId, componentId]
  );

  // append the source query param so that pdp ad units can grab
  // it to include in its kv when fetching the ad
  const updatedTiles = useMemo(
    () => updateProductTiles(productTiles, entries),
    [productTiles]
  );

  const init = () => {
    // only track product_listing event call for productGridComponent componentId, except for SRP
    if (
      componentId === "productGridComponent" &&
      typeof window !== "undefined" &&
      window?.location.pathname !== "/search"
    ) {
      const pageSize = pagination?.pageSize || 36;
      const totalResults = pagination?.totalResults || 0;
      if (updatedTiles?.length) {
        const updatedProductTiles = updatedTiles?.map(
          (product: ISDMProductTileData) => {
            return {
              ...product,
              quantity: entries[product.productId]?.quantity || 0,
            };
          }
        );
        trackPaginationFn({
          pagination: {
            currentPage: pagination?.pageNumber || 0,
            pageSize,
            totalResults,
          },
          products: updatedProductTiles,
        });
      }
    }
  };

  useEffect(() => {
    init();
  }, [pagination, updatedTiles]);

  /** accessibility messages for add to cart */
  const a11yCartMessages = useGetA11yCartMessages();
  // Create an instance of the analytics atc tracking function
  const trackFn = useAddToCartTrackEvent(EnableHeliosSnowplow);
  // Create a wrapper over the tracking function to pass productData
  const trackFnWrapper = useCallback(
    (type: string, context: unknown): void => {
      trackMediaAnalyticsEvent(
        type,
        context,
        updatedTiles,
        compData,
        EnableHeliosSnowplow,
        trackFn,
        trackMediaAnalyticsAdClick,
        pagination
      );
    },
    [updatedTiles, compData, pagination, EnableHeliosSnowplow]
  );

  const { router } = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);

  const tileClickHandler = (
    redirectUrl: string,
    isSponsored: boolean,
    productId: string
  ) => {
    trackEventTileAdClick &&
      trackEventTileAdClick(componentId || "", {
        isSponsored,
        productId,
      });
    redirectToPage(redirectUrl);
  };

  if (productTiles.length === 0) {
    return (
      <LongText
        variantId="LARGE"
        textColour="BLACK"
        markdownContentBody={`**0** ` + i18n("zeroResultsFound")}
        textAlignment="left"
      />
    );
  }

  return (
    <>
      {headerGroup && (
        <WrapperComponent styling={headerGroup.styling} isNoPadding>
          <HeaderGroupComponent {...headerGroup} noWrapper />
        </WrapperComponent>
      )}
      <ProductGrid
        columnGap={isDesktopView && !settings.isDrawer ? 24 : 1}
        p={0}
        id={id}
      >
        {updatedTiles?.map(
          (productDetails: ISDMProductTileData, index: number) => {
            const {
              brand,
              title,
              packageSizing,
              deal,
              mopDeal,
              inventoryIndicator,
              productId,
              ratings,
              productImage,
            } = productDetails;
            // TODO: remove type any after HOTM changes are complete
            const dealObj = deal as any;
            const mopDealObj = mopDeal as any;
            const tileProps = {
              ...productDetails,
              brandImageUrl: productImage[0]?.mediumRetinaUrl.replace(
                "a01_@2",
                "a01"
              ),
              quantity: entries[productId]?.quantity,
              isDisabled:
                inventoryIndicator?.indicatorId === SDMIndicatorIdType.OUT,
              dealText: deal?.text,
              inventoryIndicatorText: inventoryIndicator?.text,
              deal: dealObj,
              rating: ratings?.average_rating,
              mopDeal: mopDealObj,
            };

            return (
              <GridItem key={index}>
                <ProductTileSdm
                  language={settings.locale as SDMLanguageType}
                  {...(tileProps as any)}
                  updateCart={addToCart}
                  a11yMessages={a11yCartMessages}
                  trackingFn={trackFnWrapper}
                  isCartLoading={isCartLoading}
                  brandImageAltText={`${brand} ${title} ${packageSizing}`}
                  productTileAction={(redirectPageUrl: string) =>
                    tileClickHandler(
                      redirectPageUrl,
                      !!productDetails?.isSponsored,
                      productId
                    )
                  }
                />
              </GridItem>
            );
          }
        )}
      </ProductGrid>
    </>
  );
};

export default SimpleProductGridComponent;
